import React, { useState } from 'react';
import { 
    BookOpen, 
    Mail, 
    Image as ImageIcon, 
    Database, 
    Key, 
    History, 
    Eye, 
    EyeOff, 
    Copy, 
    Check, 
    ExternalLink,
    AlertTriangle,
    Info,
    ArrowLeft,
    Terminal,
    Settings,
    FileText,
    User,
    Code,
    CheckCircle,
    Calendar,
    HelpCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface Account {
    service: string;
    url?: string;
    user: string;
    pass: string;
    notes?: string;
    color: string;
}

export default function Documents() {
    // viewMode can be 'admin' (for client/non-technical) or 'dev' (for developers/technical)
    const [viewMode, setViewMode] = useState<'admin' | 'dev'>('admin');
    
    // Tabs for Admin Mode
    const [activeAdminTab, setActiveAdminTab] = useState<'intro' | 'manage' | 'photos' | 'bookings' | 'users'>('intro');
    
    // Tabs for Dev Mode
    const [activeDevTab, setActiveDevTab] = useState<'git' | 'emails' | 'supabase' | 'accounts' | 'changelog'>('git');
    
    const [showPass, setShowPass] = useState<Record<number, boolean>>({});
    const [copiedIndex, setCopiedIndex] = useState<Record<string, boolean>>({});

    const handleTogglePass = (index: number) => {
        setShowPass(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const handleCopy = (text: string, key: string) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(prev => ({ ...prev, [key]: true }));
        setTimeout(() => {
            setCopiedIndex(prev => ({ ...prev, [key]: false }));
        }, 2000);
    };

    const accounts: Account[] = [
        {
            service: 'Supabase (Base de datos & Auth)',
            url: 'https://supabase.com/dashboard/project/iuixlsqrlfkfnvnckgij',
            user: 'fisuraexpeditions@gmail.com',
            pass: 'PatagonClimbing2026@Chile',
            notes: 'Almacena expediciones, reservas y correos pendientes.',
            color: 'from-[#3ecf8e]/20 to-[#3ecf8e]/5 text-[#3ecf8e] border-[#3ecf8e]/30'
        },
        {
            service: 'Gmail / Google Analytics',
            url: 'https://analytics.google.com',
            user: 'fisuraexpeditions@gmail.com',
            pass: 'PatagonClimb2026@Chile',
            notes: 'Métricas de visitas y cuenta de Google vinculada.',
            color: 'from-[#ea4335]/20 to-[#ea4335]/5 text-[#ea4335] border-[#ea4335]/30'
        },
        {
            service: 'Resend (Envío de Correos)',
            url: 'https://resend.com',
            user: 'hello@fisuraexpeditions.com',
            pass: 'PatagonClimb2026@Chile',
            notes: 'API Key: re_DNW8VGd6_5ALgRkMjzPKidHxQv4RPx2eb',
            color: 'from-white/10 to-white/5 text-white border-white/20'
        },
        {
            service: 'Behold.so (Feed de Instagram)',
            url: 'https://behold.so',
            user: 'fisuraexpeditions@gmail.com',
            pass: 'PatagonClimb2026@Chile',
            notes: 'Mantiene sincronizado el widget de fotos del Home.',
            color: 'from-[#e1306c]/20 to-[#e1306c]/5 text-[#e1306c] border-[#e1306c]/30'
        },
        {
            service: 'Make.com (Automatizaciones)',
            url: 'https://make.com',
            user: 'fisuraexpeditions@gmail.com',
            pass: 'Acceso vía Google Sign-In',
            notes: 'Para flujos de trabajo auxiliares.',
            color: 'from-[#6c5ce7]/20 to-[#6c5ce7]/5 text-[#6c5ce7] border-[#6c5ce7]/30'
        },
        {
            service: 'Vercel (Alojamiento Web)',
            url: 'https://vercel.com',
            user: 'fisuraexpeditions@gmail.com',
            pass: 'Acceso directo vía GitHub/Email',
            notes: 'Alojamiento del frontend de la web en vivo.',
            color: 'from-[#000000]/40 to-[#000000]/10 text-white border-white/10 bg-black/20'
        }
    ];

    const variables = [
        { name: 'id', type: 'TEXT', desc: 'Identificador único de la expedición (Ej: laguna)' },
        { name: 'slug', type: 'TEXT', desc: 'Ruta URL descriptiva (Ej: trekking-laguna-cerro-castillo)' },
        { name: 'category', type: 'TEXT', desc: 'Categoría principal (trekking, climbing, water, wellness)' },
        { name: 'name_es / name_en', type: 'TEXT', desc: 'Nombre comercial de la ruta en español e inglés' },
        { name: 'desc_es / desc_en', type: 'TEXT', desc: 'Descripción e introducción de la expedición' },
        { name: 'difficulty', type: 'TEXT', desc: 'Nivel de dificultad física (low, mid, high)' },
        { name: 'duration', type: 'TEXT', desc: 'Duración en texto (Ej: 4 Días / 3 Noches o Full Day)' },
        { name: 'duration_type', type: 'TEXT', desc: 'Clasificación de duración (half_day, full_day, multi_day)' },
        { name: 'itinerary_type', type: 'TEXT', desc: 'Tipo de itinerario a renderizar (daily, hourly, none)' },
        { name: 'distance', type: 'TEXT', desc: 'Distancia total del recorrido (Ej: ~53 km)' },
        { name: 'price_from', type: 'NUMERIC', desc: 'Precio base en USD (Ej: 170)' },
        { name: 'price_flat', type: 'NUMERIC', desc: 'Precio fijo por persona (ignora tiers si está activo)' },
        { name: 'price_upgrades', type: 'JSONB', desc: 'Lista de opcionales (Ej: Arriendo de mochila, carpa)' },
        { name: 'pricing_tiers', type: 'JSONB', desc: 'Rangos de precios según número de personas' },
        { name: 'itinerary_es / itinerary_en', type: 'JSONB', desc: 'Detalle de actividades por día o por hora' },
        { name: 'includes_es / includes_en', type: 'TEXT[]', desc: 'Elementos que incluye la expedición' },
        { name: 'excludes_es / excludes_en', type: 'TEXT[]', desc: 'Elementos excluidos' },
        { name: 'recommendations_es / recommendations_en', type: 'TEXT[]', desc: 'Consejos de vestimenta y seguridad' },
        { name: 'policies_es / policies_en', type: 'TEXT[]', desc: 'Políticas comerciales o de impacto mínimo' },
        { name: 'image', type: 'TEXT', desc: 'Enlace de la imagen de portada (Supabase Storage)' },
        { name: 'gallery', type: 'JSONB', desc: 'Lista de imágenes secundarias con sus respectivos alts' },
        { name: 'is_visible', type: 'BOOLEAN', desc: 'Define si la expedición se muestra en la web pública' }
    ];

    const changelog = [
        {
            date: 'Junio 2026',
            title: 'Auto-generación Inteligente de ID/Slug',
            desc: 'Se implementó la reorganización del editor en el Backoffice. Ahora el Nombre va primero, y tanto el ID único como el Slug se autogeneran con políticas de prevención de colisiones local (sufijos automáticos con advertencia) y badges de estado Auto/Manual con posibilidad de restaurar.',
            badge: 'Feature'
        },
        {
            date: 'Junio 2026',
            title: 'Columnas de Layout Adaptable en Base de Datos',
            desc: 'Se extendió la tabla de expeditions de Supabase agregando columnas para configurar el tipo de itinerario (por horas, por días o ninguno), tipo de duración (media jornada, día completo, multi-día), precio fijo opcional, upgrades de tarifas y listas de recomendaciones y políticas por expedición.',
            badge: 'Database'
        },
        {
            date: 'Mayo 2026',
            title: 'Automatización de Correos Post-Reserva',
            desc: 'Integración con Resend a través de una Supabase Edge Function diaria. Envía de manera automática plantillas premium en HTML/CSS a los clientes el día posterior a la finalización de su expedición para recopilar reseñas.',
            badge: 'Emails'
        },
        {
            date: 'Abril 2026',
            title: 'Lanzamiento del Gestor del Backoffice',
            desc: 'Creación del panel de administración en `/admin` con módulos para expediciones, reservas y equipo de guías. Integración inicial con Supabase Storage para subida de fotos en la Carretera Austral.',
            badge: 'Core'
        }
    ];

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-[#f2f2f2] font-sans antialiased">
            {/* Top Bar Navigation */}
            <header className="border-b border-[#2d2621] bg-black/60 backdrop-blur-md sticky top-0 z-50 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link 
                        to="/admin" 
                        className="p-2 hover:bg-white/5 text-gray-400 hover:text-white transition-all rounded-[10px] flex items-center gap-2 text-xs uppercase font-bold tracking-widest"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Volver a Admin</span>
                    </Link>
                    <div className="h-6 w-px bg-[#2d2621]" />
                    <h1 className="text-sm font-black uppercase tracking-[0.25em] text-[#00d4ff] flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#00d4ff]" />
                        <span>Fisura Expeditions Docs</span>
                    </h1>
                </div>

                {/* Vista Mode Toggle Selector */}
                <div className="flex bg-[#2d2621]/60 p-1 rounded-[10px] border border-[#2d2621] gap-1">
                    <button 
                        onClick={() => setViewMode('admin')}
                        className={`flex items-center gap-1.5 px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-[8px] transition-all duration-300 ${
                            viewMode === 'admin' 
                            ? 'bg-[#00d4ff] text-black shadow-md' 
                            : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        <User className="w-3.5 h-3.5" />
                        <span>Administrador (Manual de Uso)</span>
                    </button>
                    <button 
                        onClick={() => setViewMode('dev')}
                        className={`flex items-center gap-1.5 px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-[8px] transition-all duration-300 ${
                            viewMode === 'dev' 
                            ? 'bg-[#00d4ff] text-black shadow-md' 
                            : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        <Code className="w-3.5 h-3.5" />
                        <span>Desarrollador (Técnico)</span>
                    </button>
                </div>
            </header>

            <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 p-6 lg:p-12">
                
                {/* Navigation Sidebar */}
                <aside className="w-full lg:w-72 shrink-0 flex flex-col gap-2">
                    {viewMode === 'admin' ? (
                        <>
                            <p className="text-[9px] font-bold text-gray-600 uppercase tracking-[0.2em] mb-2 px-4">Guías de Usuario (No Técnico)</p>
                            
                            <button 
                                onClick={() => setActiveAdminTab('intro')}
                                className={`flex items-center gap-3 px-4 py-3.5 text-xs font-bold uppercase tracking-widest rounded-[10px] transition-all text-left ${
                                    activeAdminTab === 'intro' 
                                    ? 'bg-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/10' 
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                <BookOpen className="w-4 h-4" />
                                <span>Conceptos de la Web</span>
                            </button>

                            <button 
                                onClick={() => setActiveAdminTab('manage')}
                                className={`flex items-center gap-3 px-4 py-3.5 text-xs font-bold uppercase tracking-widest rounded-[10px] transition-all text-left ${
                                    activeAdminTab === 'manage' 
                                    ? 'bg-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/10' 
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                <Settings className="w-4 h-4" />
                                <span>Administrar Rutas</span>
                            </button>

                            <button 
                                onClick={() => setActiveAdminTab('photos')}
                                className={`flex items-center gap-3 px-4 py-3.5 text-xs font-bold uppercase tracking-widest rounded-[10px] transition-all text-left ${
                                    activeAdminTab === 'photos' 
                                    ? 'bg-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/10' 
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Subida de Fotos</span>
                            </button>

                            <button 
                                onClick={() => setActiveAdminTab('bookings')}
                                className={`flex items-center gap-3 px-4 py-3.5 text-xs font-bold uppercase tracking-widest rounded-[10px] transition-all text-left ${
                                    activeAdminTab === 'bookings' 
                                    ? 'bg-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/10' 
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                <Calendar className="w-4 h-4" />
                                <span>Gestión de Reservas</span>
                            </button>

                            <button 
                                onClick={() => setActiveAdminTab('users')}
                                className={`flex items-center gap-3 px-4 py-3.5 text-xs font-bold uppercase tracking-widest rounded-[10px] transition-all text-left ${
                                    activeAdminTab === 'users' 
                                    ? 'bg-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/10' 
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                <User className="w-4 h-4" />
                                <span>Gestionar Accesos</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="text-[9px] font-bold text-gray-600 uppercase tracking-[0.2em] mb-2 px-4">Guías de Programación (Técnico)</p>
                            
                            <button 
                                onClick={() => setActiveDevTab('git')}
                                className={`flex items-center gap-3 px-4 py-3.5 text-xs font-bold uppercase tracking-widest rounded-[10px] transition-all text-left ${
                                    activeDevTab === 'git' 
                                    ? 'bg-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/10' 
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                <Terminal className="w-4 h-4" />
                                <span>Código y Git (Consola)</span>
                            </button>

                            <button 
                                onClick={() => setActiveDevTab('emails')}
                                className={`flex items-center gap-3 px-4 py-3.5 text-xs font-bold uppercase tracking-widest rounded-[10px] transition-all text-left ${
                                    activeDevTab === 'emails' 
                                    ? 'bg-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/10' 
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                <Mail className="w-4 h-4" />
                                <span>Edge Functions & DNS</span>
                            </button>

                            <button 
                                onClick={() => setActiveDevTab('supabase')}
                                className={`flex items-center gap-3 px-4 py-3.5 text-xs font-bold uppercase tracking-widest rounded-[10px] transition-all text-left ${
                                    activeDevTab === 'supabase' 
                                    ? 'bg-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/10' 
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                <Database className="w-4 h-4" />
                                <span>BD & Variables</span>
                            </button>

                            <button 
                                onClick={() => setActiveDevTab('accounts')}
                                className={`flex items-center gap-3 px-4 py-3.5 text-xs font-bold uppercase tracking-widest rounded-[10px] transition-all text-left ${
                                    activeDevTab === 'accounts' 
                                    ? 'bg-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/10' 
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                <Key className="w-4 h-4" />
                                <span>Claves y Accesos</span>
                            </button>

                            <button 
                                onClick={() => setActiveDevTab('changelog')}
                                className={`flex items-center gap-3 px-4 py-3.5 text-xs font-bold uppercase tracking-widest rounded-[10px] transition-all text-left ${
                                    activeDevTab === 'changelog' 
                                    ? 'bg-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/10' 
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                <History className="w-4 h-4" />
                                <span>Historial de Cambios</span>
                            </button>
                        </>
                    )}
                </aside>

                {/* Content Panel Area */}
                <main className="flex-1 bg-[#1a1512]/40 border border-[#2d2621] p-8 lg:p-12 rounded-[15px] shadow-2xl relative overflow-hidden backdrop-blur-sm">
                    
                    {/* Background faint gradient accent */}
                    <div className="absolute top-0 right-0 w-96 h-96 bg-[#00d4ff]/5 rounded-full filter blur-[80px] pointer-events-none" />

                    {/* RENDER ADMIN MODE TABS */}
                    {viewMode === 'admin' && (
                        <>
                            {activeAdminTab === 'intro' && (
                                <div className="space-y-8 animate-in fade-in duration-500">
                                    <div>
                                        <h2 className="text-3xl font-title uppercase tracking-tight text-white mb-2">🏔️ Guía del Administrador: Conceptos Básicos</h2>
                                        <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">Cómo funciona tu página web y de qué manera la visualizan tus clientes.</p>
                                    </div>

                                    <div className="border-t border-[#2d2621] pt-6 space-y-6 text-sm text-gray-300 leading-relaxed">
                                        <p>
                                            Tu sitio web es dinámico y se adapta de forma automática al tamaño de la pantalla (celulares, tablets o computadoras) y al idioma del explorador del cliente.
                                        </p>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                                            <div className="bg-black/30 border border-[#2d2621] p-5 rounded-[10px] space-y-2">
                                                <h4 className="font-title uppercase text-[#00d4ff] font-bold text-xs tracking-wider">Idiomas Automáticos</h4>
                                                <p className="text-xs text-gray-400 leading-relaxed">
                                                    El cliente verá la web en su idioma nativo de inmediato. Si es de Brasil verá Portugués, si es de Francia verá Francés, etc.
                                                </p>
                                            </div>

                                            <div className="bg-black/30 border border-[#2d2621] p-5 rounded-[10px] space-y-2">
                                                <h4 className="font-title uppercase text-[#00d4ff] font-bold text-xs tracking-wider">Foco Visual Premium</h4>
                                                <p className="text-xs text-gray-400 leading-relaxed">
                                                    La web destaca las bellezas naturales de la Región de Aysén mediante galerías de alta definición y textos explicativos estructurados.
                                                </p>
                                            </div>

                                            <div className="bg-black/30 border border-[#2d2621] p-5 rounded-[10px] space-y-2">
                                                <h4 className="font-title uppercase text-[#00d4ff] font-bold text-xs tracking-wider">Flujo de Reservas</h4>
                                                <p className="text-xs text-gray-400 leading-relaxed">
                                                    El cliente calcula el valor final del grupo de forma interactiva y envía una solicitud. Tú recibes y coordinas la fecha y el pago final.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="border-t border-[#2d2621] pt-6 space-y-3">
                                            <h3 className="text-lg font-title uppercase text-white">Secciones Principales de la Web</h3>
                                            <ul className="space-y-2 list-disc pl-5 text-gray-400 text-xs">
                                                <li><strong className="text-white">Inicio (Home):</strong> Muestra la propuesta de valor de Fisura Expeditions, guías e imágenes de presentación.</li>
                                                <li><strong className="text-white">Expediciones (Catálogo):</strong> Catálogo general de las experiencias organizadas por tipo (Trekking, Escalada, Agua, Wellness).</li>
                                                <li><strong className="text-white">Detalle de Experiencia:</strong> Contiene la información clave para vender: duración, qué incluye, qué no, itinerario y tabla de valores.</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeAdminTab === 'manage' && (
                                <div className="space-y-8 animate-in fade-in duration-500">
                                    <div>
                                        <h2 className="text-3xl font-title uppercase tracking-tight text-white mb-2">⚙️ Agregar o Modificar Rutas (Expediciones)</h2>
                                        <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">Instrucciones sencillas para editar tu catálogo desde el panel de control.</p>
                                    </div>

                                    <div className="border-t border-[#2d2621] pt-6 space-y-6 text-sm text-gray-300 leading-relaxed">
                                        
                                        <div className="bg-[#00d4ff]/5 border border-[#00d4ff]/25 p-5 rounded-[10px] space-y-2 flex gap-3.5 items-start">
                                            <Info className="w-6 h-6 text-[#00d4ff] shrink-0 mt-0.5" />
                                            <div>
                                                <h4 className="font-title text-xs uppercase text-[#00d4ff] font-black tracking-widest">Traductor Inteligente</h4>
                                                <p className="text-gray-400 text-xs leading-relaxed mt-1">
                                                    ¡No necesitas escribir todo en múltiples idiomas! Al crear o editar una ruta en el panel, **sólo debes llenar los textos en Español**. Al hacer clic en **Guardar**, el sistema traducirá todos los campos automáticamente al inglés, portugués, francés, italiano y alemán.
                                                </p>
                                            </div>
                                        </div>

                                        <section className="space-y-4">
                                            <h3 className="text-lg font-title uppercase text-white">Pasos para Crear una Nueva Ruta</h3>
                                            
                                            <div className="space-y-4">
                                                <div className="flex gap-4">
                                                    <span className="bg-[#2d2621] border border-gray-700 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">1</span>
                                                    <div>
                                                        <h4 className="font-bold text-white text-xs uppercase tracking-wider">Escribir el Nombre</h4>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            Ingresa el nombre en español (ej. *Trekking Laguna Cerro Castillo*). Al escribirlo, el panel generará de forma automática un **ID único** y un **Slug** (ruta de la web) en las casillas de abajo.
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex gap-4">
                                                    <span className="bg-[#2d2621] border border-gray-700 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">2</span>
                                                    <div>
                                                        <h4 className="font-bold text-white text-xs uppercase tracking-wider">Ajustar ID y Slug (Opcional)</h4>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            El sistema asegura que estas variables sean únicas y correctas. Si el nombre ya existe en tu base de datos, verás una advertencia amarilla informándote que se agregará un número automático (ej. `laguna-2`) para evitar fallos. Puedes editar estos campos a tu gusto si deseas personalizarlos.
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex gap-4">
                                                    <span className="bg-[#2d2621] border border-gray-700 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">3</span>
                                                    <div>
                                                        <h4 className="font-bold text-white text-xs uppercase tracking-wider">Rellenar Info, Precios e Itinerario</h4>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            Selecciona la dificultad, escribe la duración y ajusta la tabla de precios (cuánto cuesta para 1, 2 o más personas). Luego en las otras pestañas de la izquierda puedes agregar el itinerario detallado paso a paso, los elementos incluidos y excluidos.
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex gap-4">
                                                    <span className="bg-[#2d2621] border border-gray-700 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">4</span>
                                                    <div>
                                                        <h4 className="font-bold text-white text-xs uppercase tracking-wider">Guardar</h4>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            Haz clic en el botón azul **Guardar**. Verás una barra de progreso que indica que se están realizando las traducciones correspondientes. ¡Y listo! La ruta estará publicada en tiempo real en la web.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            )}

                            {activeAdminTab === 'photos' && (
                                <div className="space-y-8 animate-in fade-in duration-500">
                                    <div>
                                        <h2 className="text-3xl font-title uppercase tracking-tight text-white mb-2">📸 Manual de Fotos y Archivos</h2>
                                        <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">Cómo cargar imágenes de tus tours para que siempre se vean profesionales.</p>
                                    </div>

                                    <div className="border-t border-[#2d2621] pt-6 space-y-6 text-sm text-gray-300 leading-relaxed">
                                        <p>
                                            Tu sitio web de aventura atrae a los clientes mediante el impacto visual de las montañas de Aysén. Sigue estas pautas para mantener un alto estándar estético:
                                        </p>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="bg-black/30 border border-[#2d2621] p-5 rounded-[10px] space-y-2">
                                                <h4 className="font-title text-xs uppercase text-white font-bold tracking-wider">Foto de Portada</h4>
                                                <p className="text-xs text-gray-400 leading-relaxed">
                                                    Es la primera imagen que ve el cliente. Usa siempre fotografías horizontales (paisajes amplios, montañas, lagunas) tomadas con buena luz natural. Evita retratos verticales o selfis recortadas para este fin.
                                                </p>
                                            </div>

                                            <div className="bg-black/30 border border-[#2d2621] p-5 rounded-[10px] space-y-2">
                                                <h4 className="font-title text-xs uppercase text-white font-bold tracking-wider">Fotos de Galería</h4>
                                                <p className="text-xs text-gray-400 leading-relaxed">
                                                    Aquí puedes combinar fotos horizontales con retratos verticales de acción (ej. escaladores en roca, senderistas con mochilas). Asegúrate de que las imágenes muestren la experiencia real del servicio.
                                                </p>
                                            </div>
                                        </div>

                                        <section className="space-y-4">
                                            <h3 className="text-lg font-title uppercase text-white">Pasos para subir fotos al editor:</h3>
                                            <ol className="list-decimal pl-5 text-xs text-gray-400 space-y-3">
                                                <li>Dentro del editor de la expedición, haz clic en la pestaña <strong className="text-white">Fotos</strong> en el menú lateral.</li>
                                                <li>Presiona el botón <strong className="text-white">Seleccionar Archivos</strong> o arrastra las fotos directamente al recuadro.</li>
                                                <li>El sistema subirá la imagen directamente al servidor en la nube de Supabase. Verás aparecer las miniaturas de las imágenes en pocos segundos.</li>
                                                <li><strong className="text-white">Eliminar imágenes:</strong> Si deseas borrar una foto que subiste por error, haz clic en el ícono del basurero rojo sobre la miniatura y confirma la acción.</li>
                                            </ol>
                                        </section>
                                    </div>
                                </div>
                            )}

                            {activeAdminTab === 'bookings' && (
                                <div className="space-y-8 animate-in fade-in duration-500">
                                    <div>
                                        <h2 className="text-3xl font-title uppercase tracking-tight text-white mb-2">📅 Gestión de Reservas y Clientes</h2>
                                        <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">Cómo responder a las solicitudes e interactuar con los exploradores.</p>
                                    </div>

                                    <div className="border-t border-[#2d2621] pt-6 space-y-6 text-sm text-gray-300 leading-relaxed">
                                        <section className="space-y-3">
                                            <h3 className="text-lg font-title uppercase text-[#00d4ff]">El ciclo de la reserva</h3>
                                            <p className="text-gray-300 text-sm leading-relaxed">
                                                Cuando un cliente desea participar en una de tus expediciones, ingresa su correo, cantidad de personas y fecha de inicio en la web:
                                            </p>
                                            <ul className="list-disc pl-5 text-xs text-gray-400 space-y-2">
                                                <li>El cliente recibe una confirmación visual en pantalla que indica que su interés ha sido registrado con éxito.</li>
                                                <li>La reserva entra a tu panel administrativo (**Reservas**) en estado de espera.</li>
                                                <li>Tú recibes la solicitud, te pones en contacto con el explorador para definir el método de pago y las condiciones de la ruta, y confirmas la reserva en el sistema una vez asegurado el pago.</li>
                                            </ul>
                                        </section>

                                        <section className="space-y-3">
                                            <h3 className="text-lg font-title uppercase text-[#00d4ff] flex items-center gap-1.5">
                                                <CheckCircle className="w-5 h-5 text-[#00d4ff]" /> Encuesta de Feedback Automática
                                            </h3>
                                            <p className="text-gray-300 text-sm leading-relaxed">
                                                ¡El sistema fideliza al cliente por ti! Al día siguiente de que finalice la fecha de su expedición contratada, la web le enviará un correo con un enlace personalizado para que pueda calificar su experiencia, dejándote comentarios valiosos para mejorar el servicio y recopilar reseñas positivas.
                                            </p>
                                        </section>
                                    </div>
                                </div>
                            )}

                            {activeAdminTab === 'users' && (
                                <div className="space-y-8 animate-in fade-in duration-500">
                                    <div>
                                        <h2 className="text-3xl font-title uppercase tracking-tight text-white mb-2">👥 Agregar y Administrar Usuarios (Accesos)</h2>
                                        <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">Cómo permitir que nuevas personas inicien sesión en el Backoffice.</p>
                                    </div>

                                    <div className="border-t border-[#2d2621] pt-6 space-y-6 text-sm text-gray-300 leading-relaxed">
                                        <p>
                                            El Backoffice de Fisura Expeditions está protegido para evitar accesos no autorizados. Para que un nuevo miembro del equipo pueda ingresar, debes registrar su correo en la plataforma de <strong>Supabase</strong>.
                                        </p>

                                        <div className="bg-[#00d4ff]/5 border border-[#00d4ff]/25 p-5 rounded-[10px] space-y-2 flex gap-3.5 items-start">
                                            <Info className="w-6 h-6 text-[#00d4ff] shrink-0 mt-0.5" />
                                            <div>
                                                <h4 className="font-title text-xs uppercase text-[#00d4ff] font-black tracking-widest">Enlace Directo a Supabase</h4>
                                                <p className="text-gray-400 text-xs leading-relaxed mt-1">
                                                    Puedes hacer clic en el botón de abajo para ir directamente al panel de usuarios en Supabase (deberás iniciar sesión con las credenciales que se muestran en la sección "Claves y Accesos" si no has ingresado antes).
                                                </p>
                                                <a 
                                                    href="https://supabase.com/dashboard/project/iuixlsqrlfkfnvnckgij/auth/users" 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 mt-3 bg-[#00d4ff] text-black px-4 py-2 rounded-[8px] font-bold text-xs uppercase tracking-wider hover:bg-white transition-all shadow-md"
                                                >
                                                    <span>Ir a Usuarios en Supabase</span>
                                                    <ExternalLink className="w-4 h-4" />
                                                </a>
                                            </div>
                                        </div>

                                        <section className="space-y-4">
                                            <h3 className="text-lg font-title uppercase text-white">Guía Paso a Paso para Crear un Usuario</h3>
                                            
                                            <div className="space-y-4">
                                                <div className="flex gap-4">
                                                    <span className="bg-[#2d2621] border border-gray-700 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">1</span>
                                                    <div>
                                                        <h4 className="font-bold text-white text-xs uppercase tracking-wider">Ingresar a Supabase</h4>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            Ve a la pestaña <strong>Authentication</strong> en la barra lateral izquierda del panel de Supabase (tiene un ícono de llave o escudo).
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex gap-4">
                                                    <span className="bg-[#2d2621] border border-gray-700 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">2</span>
                                                    <div>
                                                        <h4 className="font-bold text-white text-xs uppercase tracking-wider">Hacer clic en "Add User"</h4>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            En la parte superior derecha de la lista de usuarios, haz clic en el botón azul <strong>Add User</strong> y selecciona <strong>Create User</strong>.
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex gap-4">
                                                    <span className="bg-[#2d2621] border border-gray-700 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">3</span>
                                                    <div>
                                                        <h4 className="font-bold text-white text-xs uppercase tracking-wider">Llenar los Datos del Nuevo Usuario</h4>
                                                        <ul className="list-disc pl-5 mt-1 space-y-1.5 text-xs text-gray-400">
                                                            <li><strong>Email:</strong> Escribe el correo electrónico del nuevo usuario (ej: <code className="text-gray-300">ejemplo@fisuraexpeditions.com</code>).</li>
                                                            <li><strong>Password:</strong> Escribe una contraseña provisoria segura. El usuario podrá cambiarla después.</li>
                                                            <li><strong>Auto-confirm User:</strong> Asegúrate de dejar esta casilla <strong>marcada (Checked)</strong> si deseas que la cuenta se active de inmediato sin requerir confirmación por correo electrónico.</li>
                                                        </ul>
                                                    </div>
                                                </div>

                                                <div className="flex gap-4">
                                                    <span className="bg-[#2d2621] border border-gray-700 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">4</span>
                                                    <div>
                                                        <h4 className="font-bold text-white text-xs uppercase tracking-wider">Confirmar la Creación</h4>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            Haz clic en <strong>Save</strong> o <strong>Create User</strong>. ¡Listo! El nuevo miembro ya puede ingresar a <code className="text-[#00d4ff]">fisuraexpeditions.com/login</code> con su correo y contraseña.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* RENDER DEV MODE TABS */}
                    {viewMode === 'dev' && (
                        <>
                            {activeDevTab === 'git' && (
                                <div className="space-y-8 animate-in fade-in duration-500">
                                    <div>
                                        <h2 className="text-3xl font-title uppercase tracking-tight text-[#00d4ff] mb-2">💻 Control de Versiones y Consola Git</h2>
                                        <p className="text-xs text-amber-500 uppercase tracking-widest font-bold flex items-center gap-1.5">
                                            <AlertTriangle className="w-3.5 h-3.5" /> Sección reservada exclusivamente para programadores y soporte técnico.
                                        </p>
                                    </div>

                                    <div className="border-t border-[#2d2621] pt-6 space-y-6 text-sm text-gray-300 leading-relaxed">
                                        <section className="space-y-3">
                                            <h3 className="text-lg font-title uppercase text-white">Flujo de Trabajo del Código</h3>
                                            <p>
                                                Los cambios realizados en los archivos de la aplicación React (páginas, componentes, estilos o scripts locales) se gestionan localmente en la terminal utilizando comandos Git.
                                            </p>
                                        </section>

                                        <section className="space-y-3">
                                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Cómo guardar y publicar actualizaciones de código:</h4>
                                            
                                            <div className="space-y-4">
                                                <div className="bg-black/60 border border-[#2d2621] p-5 rounded-[10px] space-y-2">
                                                    <span className="text-[10px] font-black uppercase text-[#00d4ff] bg-[#00d4ff]/10 px-2.5 py-1 rounded-[5px] inline-block font-sans mb-1">Paso 1: Añadir cambios</span>
                                                    <p className="text-xs text-gray-400 leading-relaxed mb-2">
                                                        Este comando añade todos los archivos modificados o creados a la pila de cambios listos para ser guardados en la versión actual.
                                                    </p>
                                                    <div className="bg-black p-3.5 rounded-[6px] font-mono text-xs text-[#3ecf8e]">
                                                        git add .
                                                    </div>
                                                </div>

                                                <div className="bg-black/60 border border-[#2d2621] p-5 rounded-[10px] space-y-2">
                                                    <span className="text-[10px] font-black uppercase text-[#00d4ff] bg-[#00d4ff]/10 px-2.5 py-1 rounded-[5px] inline-block font-sans mb-1">Paso 2: Confirmar Cambios (Commit)</span>
                                                    <p className="text-xs text-gray-400 leading-relaxed mb-2">
                                                        Registra los cambios en tu historial local añadiendo una descripción clara de las mejoras para facilitar la trazabilidad.
                                                    </p>
                                                    <div className="bg-black p-3.5 rounded-[6px] font-mono text-xs text-[#3ecf8e]">
                                                        git commit -m "admin: agrega la pagina de documentacion e instructivos"
                                                    </div>
                                                </div>

                                                <div className="bg-black/60 border border-[#2d2621] p-5 rounded-[10px] space-y-2">
                                                    <span className="text-[10px] font-black uppercase text-[#00d4ff] bg-[#00d4ff]/10 px-2.5 py-1 rounded-[5px] inline-block font-sans mb-1">Paso 3: Subir Cambios (Push)</span>
                                                    <p className="text-xs text-gray-400 leading-relaxed mb-2">
                                                        Sube tus cambios confirmados desde tu computadora local al repositorio remoto (GitHub). Esto gatilla automáticamente el despliegue automático del servidor de producción (Vercel).
                                                    </p>
                                                    <div className="bg-black p-3.5 rounded-[6px] font-mono text-xs text-[#3ecf8e]">
                                                        git push
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            )}

                            {activeDevTab === 'emails' && (
                                <div className="space-y-8 animate-in fade-in duration-500">
                                    <div>
                                        <h2 className="text-3xl font-title uppercase tracking-tight text-white mb-2">✉️ Servidores de Correo: Edge Functions & DNS</h2>
                                        <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">Cómo funciona la API de Resend y la configuración de registros en Supabase Cloud.</p>
                                    </div>

                                    <div className="border-t border-[#2d2621] pt-6 space-y-6">
                                        <section className="space-y-3">
                                            <h3 className="text-lg font-title uppercase text-[#00d4ff]">Lógica de la Función Dailly</h3>
                                            <p className="text-gray-300 text-sm leading-relaxed">
                                                La función `send-booking-emails` obtiene reservas cuya fecha de término (`start_date` + duración de ruta) sea igual a la fecha de ayer. Se encarga de inyectar variables en la plantilla HTML y gatillar la petición de envío vía Resend.
                                            </p>
                                        </section>

                                        <section className="space-y-4 border-t border-[#2d2621]/40 pt-4">
                                            <h3 className="text-lg font-title uppercase text-white">Despliegue y Comandos CLI</h3>
                                            
                                            <div className="space-y-3">
                                                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Desplegar / Actualizar la función en la nube de Supabase:</p>
                                                <div className="bg-black p-4 rounded-[10px] font-mono text-xs text-[#3ecf8e] border border-[#2d2621]">
                                                    supabase functions deploy send-booking-emails
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Establecer variables de entorno seguras (API Keys):</p>
                                                <div className="bg-black p-4 rounded-[10px] font-mono text-xs text-[#3ecf8e] border border-[#2d2621]">
                                                    supabase secrets set RESEND_API_KEY=re_tu_api_key_de_resend
                                                </div>
                                            </div>
                                        </section>

                                        <section className="space-y-3 border-t border-[#2d2621]/40 pt-4">
                                            <h3 className="text-lg font-title uppercase text-white">Registros DNS requeridos en Resend (Hosting Dominio)</h3>
                                            <p className="text-gray-300 text-xs">
                                                Para certificar el dominio `fisuraexpeditions.com` en Resend, agrega los siguientes registros en la zona DNS de tu proveedor:
                                            </p>
                                            <ul className="list-disc pl-5 text-xs text-gray-400 space-y-2">
                                                <li><strong>Registro SPF (TXT):</strong> Permite a Resend firmar correos bajo tu nombre corporativo.</li>
                                                <li><strong>Registro DKIM (TXT):</strong> Clave criptográfica para certificar que el correo no fue alterado.</li>
                                                <li><strong>Registro MX:</strong> Configuración de ruteo de correos de retorno.</li>
                                            </ul>
                                        </section>
                                    </div>
                                </div>
                            )}

                            {activeDevTab === 'supabase' && (
                                <div className="space-y-8 animate-in fade-in duration-500">
                                    <div>
                                        <h2 className="text-3xl font-title uppercase tracking-tight text-white mb-2">🗄️ Base de Datos y Storage</h2>
                                        <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">Esquema de variables del catálogo de expediciones en Supabase.</p>
                                    </div>

                                    <div className="border-t border-[#2d2621] pt-6 space-y-6">
                                        <div className="overflow-x-auto rounded-[10px] border border-[#2d2621]">
                                            <table className="w-full text-left border-collapse text-xs">
                                                <thead>
                                                    <tr className="bg-black/60 border-b border-[#2d2621] text-gray-400 uppercase tracking-widest text-[9px] font-bold">
                                                        <th className="p-4">Variable</th>
                                                        <th className="p-4">Tipo</th>
                                                        <th className="p-4">Descripción</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-[#2d2621] bg-black/10">
                                                    {variables.map((v, i) => (
                                                        <tr key={i} className="hover:bg-white/5 transition-colors">
                                                            <td className="p-4 font-mono text-[#00d4ff] font-bold">{v.name}</td>
                                                            <td className="p-4 text-gray-400 font-mono font-medium">{v.type}</td>
                                                            <td className="p-4 text-gray-300">{v.desc}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeDevTab === 'accounts' && (
                                <div className="space-y-8 animate-in fade-in duration-500">
                                    <div>
                                        <h2 className="text-3xl font-title uppercase tracking-tight text-white mb-2">🔐 Cuentas y Accesos de Desarrollo</h2>
                                        <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">Credenciales e información confidencial para programadores.</p>
                                    </div>

                                    <div className="border-t border-[#2d2621] pt-6 space-y-6">
                                        <div className="border-l-4 border-amber-500/50 bg-amber-500/5 p-4 rounded-r-[10px] flex gap-3 items-start">
                                            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                                            <div>
                                                <h4 className="text-xs font-black uppercase text-amber-500 tracking-wider">Advertencia de Seguridad</h4>
                                                <p className="text-gray-400 text-[11px] leading-relaxed mt-1">
                                                    Esta sección almacena claves privadas sensibles de producción. Este archivo está localmente protegido de subidas en `.gitignore` y esta vista interactiva implementa cifrado visual temporal. Posteriormente, esta ruta será restringida bajo un inicio de sesión administrativo seguro.
                                        </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {accounts.map((acc, index) => {
                                                const isRevealed = !!showPass[index];
                                                const isCopiedPass = !!copiedIndex[`p-${index}`];
                                                const isCopiedUser = !!copiedIndex[`u-${index}`];

                                                return (
                                                    <div 
                                                        key={index}
                                                        className={`border rounded-[12px] bg-gradient-to-br p-6 space-y-4 shadow-md transition-all border-opacity-40 hover:scale-[1.01] ${acc.color}`}
                                                    >
                                                        <div className="flex justify-between items-start gap-4">
                                                            <h3 className="font-title text-sm uppercase font-bold text-white tracking-wide">{acc.service}</h3>
                                                            {acc.url && (
                                                                <a 
                                                                    href={acc.url} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer" 
                                                                    className="text-gray-400 hover:text-white transition-colors"
                                                                    title="Ir al sitio"
                                                                >
                                                                    <ExternalLink className="w-4 h-4" />
                                                                </a>
                                                            )}
                                                        </div>

                                                        <div className="space-y-3 font-mono text-[11px]">
                                                            <div className="space-y-1">
                                                                <span className="text-[9px] font-sans font-bold uppercase tracking-wider text-gray-500 block">Usuario / Correo</span>
                                                                <div className="flex items-center justify-between bg-black/40 px-3 py-2 rounded-[6px] border border-white/5">
                                                                    <span className="text-gray-200 select-all truncate mr-2">{acc.user}</span>
                                                                    <button 
                                                                        onClick={() => handleCopy(acc.user, `u-${index}`)}
                                                                        className="text-gray-500 hover:text-white transition-colors"
                                                                        title="Copiar usuario"
                                                                    >
                                                                        {isCopiedUser ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-1">
                                                                <span className="text-[9px] font-sans font-bold uppercase tracking-wider text-gray-500 block">Contraseña</span>
                                                                <div className="flex items-center justify-between bg-black/40 px-3 py-2 rounded-[6px] border border-white/5">
                                                                    <span className="text-gray-200 select-all truncate mr-2 font-mono">
                                                                        {isRevealed ? acc.pass : '••••••••••••••••'}
                                                                    </span>
                                                                    <div className="flex items-center gap-2">
                                                                        <button 
                                                                            onClick={() => handleTogglePass(index)}
                                                                            className="text-gray-500 hover:text-white transition-colors"
                                                                            title={isRevealed ? 'Ocultar clave' : 'Mostrar clave'}
                                                                        >
                                                                            {isRevealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                                                        </button>
                                                                        <button 
                                                                            onClick={() => handleCopy(acc.pass, `p-${index}`)}
                                                                            className="text-gray-500 hover:text-white transition-colors"
                                                                            title="Copiar clave"
                                                                        >
                                                                            {isCopiedPass ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {acc.notes && (
                                                            <p className="text-[10px] text-gray-400 font-sans italic pt-1 border-t border-white/5">
                                                                {acc.notes}
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeDevTab === 'changelog' && (
                                <div className="space-y-8 animate-in fade-in duration-500">
                                    <div>
                                        <h2 className="text-3xl font-title uppercase tracking-tight text-white mb-2">⏳ Hoja de Cambios (Bitácora)</h2>
                                        <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">Línea de tiempo del desarrollo y actualizaciones técnicas.</p>
                                    </div>

                                    <div className="border-t border-[#2d2621] pt-8">
                                        <div className="relative border-l border-[#2d2621] ml-4 space-y-8 pb-4">
                                            {changelog.map((ch, idx) => (
                                                <div key={idx} className="relative pl-8">
                                                    <span className="absolute -left-[9px] top-1 bg-black border-2 border-[#00d4ff] w-4.5 h-4.5 rounded-full flex items-center justify-center">
                                                        <span className="w-1.5 h-1.5 bg-[#00d4ff] rounded-full" />
                                                    </span>

                                                    <div className="space-y-2">
                                                        <div className="flex flex-wrap items-center gap-3">
                                                            <span className="text-[10px] font-black uppercase text-gray-500 tracking-wider font-mono">{ch.date}</span>
                                                            <span className="text-[8px] font-black uppercase text-[#00d4ff] bg-[#00d4ff]/10 border border-[#00d4ff]/30 px-2 py-0.5 rounded-[5px]">{ch.badge}</span>
                                                        </div>
                                                        <h4 className="text-md font-title uppercase text-white font-bold">{ch.title}</h4>
                                                        <p className="text-gray-300 text-xs leading-relaxed max-w-2xl">{ch.desc}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>

            {/* Footer accent */}
            <footer className="py-12 border-t border-[#2d2621] text-center opacity-30">
                <p className="text-[9px] font-black uppercase tracking-[0.3em]">Fisura Expeditions Guide System &copy; 2026</p>
            </footer>
        </div>
    );
}
